import { Dispatch, SetStateAction, useMemo } from 'react';

import { Box } from '@mui/material';
import { useForm } from '@tanstack/react-form';

import Button from '@/components/Buttons/Button';
import TextField from '@/components/TextField';
import Typography from '@/components/Typography';
import { spacing } from '@/constants/ui';
import StepButtons from '@/pages/Onboarding/OnboardingForm/Steps/StepButtons';
import { InviteRecipient } from '@/pages/Onboarding/types/recipient.types';
import { generateSecureRandomString } from '@/utils/generateSecureRandomString';
import { emailValidator } from '@/utils/validators';

const Invite = ({
  activeStep,
  setActiveStep,
  inviteRecipients,
  setInviteRecipients
}: {
  inviteRecipients: InviteRecipient[];
  setInviteRecipients: Dispatch<SetStateAction<InviteRecipient[]>>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}) => {
  // Ensure the form always renders at least one input row
  const initialRecipients = useMemo<InviteRecipient[]>(() => {
    const list = inviteRecipients?.length ? inviteRecipients : [];
    return list.length > 0
      ? list
      : [
          {
            email: '',
            id: generateSecureRandomString()
          }
        ];
  }, [inviteRecipients]);

  const form = useForm({
    defaultValues: {
      recipients: initialRecipients
    },
    validators: {
      onChange: ({ value }) => {
        const errors: string[] = [];
        value.recipients.forEach(recipient => {
          if (recipient.email.length > 0) {
            const errorMessage = emailValidator(recipient.email);
            if (errorMessage) {
              errors.push(errorMessage);
            }
          }
        });
        return errors.length > 0 ? { form: errors.join(', ') } : undefined;
      }
    },
    onSubmit: ({ value }) => {
      const filledRecipients = value.recipients.filter(rec => rec.email.length > 0);
      setInviteRecipients(filledRecipients);
      setActiveStep(activeStep + 1);
    }
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing['1.5']
        }}
      >
        <Typography
          size={24}
          sx={{ fontWeight: 'bold' }}
        >
          Data is more valuable when it's shared!
        </Typography>
        <Typography
          size={20}
          sx={{ fontWeight: 'bold' }}
        >
          Invite your teammates to collaborate...
        </Typography>

        <form.Field name='recipients'>
          {field => (
            <>
              {field.state.value.map((recipient, index) => (
                <form.Field
                  key={recipient.id || `${index}`}
                  name={`recipients[${index}].email`}
                  validators={{
                    onChange: ({ value }) => {
                      if (!value || value.length === 0) return undefined;
                      return emailValidator(value) || undefined;
                    }
                  }}
                >
                  {subField => {
                    const handleChange = (
                      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      subField.handleChange(e.target.value);

                      const currentRecipients = field.state.value;
                      const updatedRecipients = currentRecipients.map((rec, idx) =>
                        idx === index ? { ...rec, email: e.target.value } : rec
                      );

                      const filledRecipients = updatedRecipients.filter(
                        rec => rec.email.length > 0
                      );

                      field.handleChange(
                        filledRecipients.length === updatedRecipients.length
                          ? [...updatedRecipients, { email: '', id: generateSecureRandomString() }]
                          : updatedRecipients
                      );
                    };

                    return (
                      <TextField
                        autoComplete={`inviteEmail${recipient.id}`}
                        error={!!subField.state.meta.errors.length}
                        helperText={subField.state.meta.errors.join(', ')}
                        id={`inviteEmail${recipient.id}`}
                        label='Work email'
                        name={`inviteEmail${index}`}
                        sx={{
                          minWidth: 220
                        }}
                        value={subField.state.value}
                        variant='filled'
                        onBlur={subField.handleBlur}
                        onChange={handleChange}
                      />
                    );
                  }}
                </form.Field>
              ))}
            </>
          )}
        </form.Field>

        <form.Subscribe
          selector={state => ({
            recipients: state.values.recipients,
            isValid: state.canSubmit,
            fieldMeta: state.fieldMeta
          })}
        >
          {({ recipients, isValid }) => {
            const hasAnyEmail = recipients.some(rec => rec.email.length > 0);
            const nextDisabled = !hasAnyEmail || !isValid;

            return (
              <StepButtons
                activeStep={activeStep}
                nextDisabled={nextDisabled}
                onBackClick={() => {
                  setActiveStep(activeStep - 1);
                }}
                onNextClick={() => {
                  form.handleSubmit();
                }}
              />
            );
          }}
        </form.Subscribe>
        <Button
          fullWidth
          disabled={activeStep === 0}
          sx={{
            '&.MuiButton-outlined': {
              color: 'primary.main',
              borderColor: 'primary.main'
            }
          }}
          variant='outlined'
          onClick={() => {
            setActiveStep(activeStep + 1);
          }}
        >
          Skip for now
        </Button>
      </Box>
    </form>
  );
};
export default Invite;
